import * as React from 'react';
import { DateTime } from 'luxon';
import {
  MemberInvoice,
  MemberInvoiceData,
  MemberInvoiceDataResponse,
} from './types';
import { useGet } from 'core/api';

type Props = {
  orgId: number;
  memberId: number;
  timeZone: string;
};

export const useMemberInvoiceData = ({ orgId, memberId, timeZone }: Props) => {
  const { data: MemberInvoiceData, loading } = useGet<MemberInvoiceDataResponse>(
    `/organizations/${orgId}/members/${memberId}/invoices`
  );

  const invoicesData = React.useMemo(() => {
    if (!MemberInvoiceData || !MemberInvoiceData.data) return [];
    const { data } = MemberInvoiceData;
    return data.map((item) => mapToMemberInvoice(item));
  }, [MemberInvoiceData]);

  const invoicesKeyInsights = React.useMemo(() => {
    if (!invoicesData || invoicesData.length === 0) return null;

    const overdueAmount = invoicesData.reduce((acc, invoice) => {
      /*
        `invoice.dueDate` is in UTC but with offset of the time zone of division applied.
        The assumption is that the organization's timezone and division timezone are in sync.
        We need to convert current date time to the organization's time zone for comparison.
      */
      const dateTimeNowInOrgISO = DateTime.now().setZone(timeZone).toISO();
      if (
        invoice.balance > 0 &&
        dateTimeNowInOrgISO &&
        invoice.dueDate < dateTimeNowInOrgISO
      ) {
        return acc + invoice.balance;
      }
      return acc;
    }, 0);

    const scheduleAmount = invoicesData.reduce((acc, invoice) => {
      if (invoice.scheduledPaymentsAmount > 0) {
        return acc + invoice.scheduledPaymentsAmount;
      }
      return acc;
    }, 0);

    const outstandingAmount = invoicesData.reduce(
      (acc, invoice) => acc + invoice.balance,
      0
    );

    const paidAmount = invoicesData.reduce(
      (acc, invoice) => acc + (invoice.total - invoice.balance),
      0
    );

    return { overdueAmount, scheduleAmount, outstandingAmount, paidAmount };
  }, [invoicesData, timeZone]);

  return { invoicesKeyInsights, invoicesData, loading };
};

const mapToMemberInvoice = (
  invoiceDataItem: MemberInvoiceData
): MemberInvoice => ({
  balance: invoiceDataItem.balance,
  batchInvoiceId: invoiceDataItem.batch_invoice_id,
  dueDate: invoiceDataItem.due_date,
  name: invoiceDataItem.name,
  invoiceId: invoiceDataItem.id,
  programId: invoiceDataItem.program_id,
  programName: invoiceDataItem.program_name,
  scheduledPaymentsAmount: invoiceDataItem.scheduled_payments_amount,
  seasonId: invoiceDataItem.season_id,
  seasonName: invoiceDataItem.season_name,
  divisionId: invoiceDataItem.division_id,
  status: invoiceDataItem.status,
  total: invoiceDataItem.total,
});
